import { Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import simpletechLogoFooter from "@/assets/simpletech-logo-footer.png";
import { services } from "@/data/services";
import { RotatingVerse } from "@/components/RotatingVerse";

const socialLinks: { label: string; href: string; Icon?: typeof Facebook; initial?: string }[] = [
  { label: "Facebook", Icon: Facebook, href: "https://www.facebook.com/simplifyingtechnologyforeveryone" },
  { label: "Instagram", Icon: Instagram, href: "https://instagram.com/simplifying_technology" },
  { label: "LinkedIn", Icon: Linkedin, href: "https://www.linkedin.com/company/simpletechhelper/" },
  { label: "Google", initial: "G", href: "https://www.google.com/search?q=simpletech+montclair" },
];

export function SiteFooter() {
  return (
    <footer className="bg-[#0b0f3d] text-white pt-16 pb-6">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 pb-12">
          <div className="flex flex-col items-start">
            <img src={simpletechLogoFooter} alt="SimpleTech logo — trusted IT support, cybersecurity, and managed services for Southern California businesses" width={160} height={53} className="w-40 h-auto mb-8" />
            <div className="flex items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(s.href, "_blank", "noopener,noreferrer");
                  }}
                  className="w-9 h-9 rounded-full bg-white text-[#0b0f3d] grid place-items-center text-sm font-bold hover:bg-[#28bcff] hover:text-white transition-colors cursor-pointer"
                >
                  {s.Icon ? <s.Icon className="w-4 h-4" /> : s.initial}
                </a>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-extrabold mb-6">Services</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm text-slate-200">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  {...(s.slug === "managed-it"
                    ? { to: "/services/managed-it-services" as const }
                    : { to: "/services/$slug" as const, params: { slug: s.slug } })}
                  className="hover:text-[#28bcff]"
                >
                  {s.shortTitle}
                </Link>
              ))}
            </div>
            <h3 className="text-lg font-extrabold mt-8 mb-4">Service Areas</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-slate-200">
              {[
                { slug: "montclair", name: "Montclair, CA" },
                { slug: "rancho-cucamonga", name: "Rancho Cucamonga, CA" },
                { slug: "upland", name: "Upland, CA" },
                { slug: "claremont", name: "Claremont, CA" },
                { slug: "ontario", name: "Ontario, CA" },
              ].map((c) => (
                <Link
                  key={c.slug}
                  to="/services/managed-it-services/$city"
                  params={{ city: c.slug }}
                  className="hover:text-[#28bcff]"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-extrabold mb-6">Important</h3>
            <div className="flex flex-col gap-4 text-sm text-slate-200">
              <Link to="/" className="hover:text-[#28bcff]">Home</Link>
              <Link to="/about" className="hover:text-[#28bcff]">About</Link>
              <Link to="/services" className="hover:text-[#28bcff]">Services</Link>
              <Link to="/reviews" className="hover:text-[#28bcff]">Reviews</Link>
              <Link to="/contact" className="hover:text-[#28bcff]">Contact</Link>
              <Link to="/managed-it-services-cost-inland-empire" className="hover:text-[#28bcff]">Managed IT Pricing Guide</Link>

            </div>
          </div>
          <div className="space-y-7 text-sm">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                <Phone className="w-5 h-5 text-[#28bcff]" />
              </div>
              <div>
                <div className="text-lg font-bold mb-1">Call Us</div>
                <a href="tel:9092559112" className="text-slate-200 hover:text-[#28bcff]">(909) 255-9112</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#28bcff]" />
              </div>
              <div>
                <div className="text-lg font-bold mb-1">Email Us</div>
                <a href="mailto:contact@simpletechhelper.com" className="text-slate-200 hover:text-[#28bcff] break-all">contact@simpletechhelper.com</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#28bcff]" />
              </div>
              <div>
                <div className="text-lg font-bold mb-1">Mailing Address</div>
                <div className="text-slate-200 leading-relaxed">
                  8880 N Benson Ave #121<br />
                  Montclair, CA 91763
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center pb-4">
          <RotatingVerse className="text-sm text-white/60" />
        </div>

        <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-slate-300">
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/privacy-policy" className="hover:text-[#28bcff]">Privacy Policy</Link>
            <span className="text-white/20">|</span>
            <Link to="/terms-and-conditions" className="hover:text-[#28bcff]">Terms and Conditions</Link>
            <span className="text-white/20">|</span>
            <Link to="/accessibility-statement" className="hover:text-[#28bcff]">Accessibility</Link>
            <span className="text-white/20">|</span>
            <Link to="/sitemap" className="hover:text-[#28bcff]">Sitemap</Link>
          </div>
          <div>Copyright © {new Date().getFullYear()} SimpleTech. All Rights Reserved.</div>
        </div>
      </div>
    </footer>
  );
}
